import { v4 as uuidv4 } from 'uuid';
import { normalizeStoredImageItem } from './localImage.js';
import { normalizeLocalFileItem } from './localFile.js';
import { normalizeUploadedImageItem } from './mediaItem.js';
import { LOCAL_ASSET } from './localAssets.js';
import { getMediaItemUrl } from './qiniuMedia.js';

export const PAY_FILE_UNLOCK_TYPE = 'pay-file-unlock';
export const PAY_FILE_UNLOCK_DEFAULT_BORDER_RADIUS = 12;
export const PAY_FILE_UNLOCK_GRID_COL_COUNT = 3;

export const DEFAULT_PAY_FILE_FOOTER_FEATURES = [
	{ id: uuidv4(), title: '安全加密', subtitle: '保障文档安全', icon: 'shield' },
	{ id: uuidv4(), title: '支持下载', subtitle: '解锁后可下载', icon: 'download' },
	{ id: uuidv4(), title: '永久有效', subtitle: '一次购买终身查看', icon: 'valid' },
];

function getFixedFooterFeatures() {
	return DEFAULT_PAY_FILE_FOOTER_FEATURES.map(item => ({ ...item, id: uuidv4() }));
}

function getFileTypeLabel(fileName = '') {
	const ext = (fileName.split('.').pop() || '').toLowerCase();
	if (ext === 'pdf') return 'PDF 文档';
	if (['doc', 'docx'].includes(ext)) return 'Word 文档';
	if (['xls', 'xlsx'].includes(ext)) return 'Excel 表格';
	if (['ppt', 'pptx'].includes(ext)) return 'PPT 文档';
	return '文档';
}

function createDemoPreviewItem(index = 0) {
	const localImgs = LOCAL_ASSET.payFileUnlock;
	return {
		id: uuidv4(),
		image: {
			id: uuidv4(),
			filename: `pay-file-unlock-${index + 1}.png`,
			previewUrl: localImgs[index % localImgs.length],
			url: '',
			local: false,
		},
	};
}

export function createPayFileUnlockDemoPreviewItems(count = 6) {
	return Array.from({ length: count }, (_, index) => createDemoPreviewItem(index));
}

function createDefaultPreviewItem(preset = {}) {
	return {
		id: preset.id || uuidv4(),
		image: normalizePreviewImage(preset.image),
	};
}

export function getPayFileUnlockPreviewImageUrl(image) {
	if (!image) return '';
	return getMediaItemUrl(image);
}

export function getPayFileUnlockGalleryUrls(data = {}) {
	const normalized = normalizePayFileUnlockData(data);
	return normalized.items
		.map(item => getPayFileUnlockPreviewImageUrl(item.image))
		.filter(Boolean);
}

function normalizePreviewImage(image) {
	if (!image?.previewUrl && !image?.url) return null;
	return normalizeStoredImageItem(image);
}

function normalizePreviewItem(item = {}) {
	return {
		id: item.id || uuidv4(),
		image: normalizePreviewImage(item.image),
	};
}

function normalizeSourceFile(file) {
	if (!file) return null;
	return normalizeLocalFileItem(file);
}

function normalizePayFileFields(data = {}) {
	const sourceItems = Array.isArray(data.items)
		? data.items
		: (Array.isArray(data.previewItems) ? data.previewItems : []);
	const items = sourceItems.map(normalizePreviewItem).filter(item => item.image);
	const pageCount = items.length;
	const freePreviewCountRaw = Number(data.freePreviewCount);
	const freePreviewCount = Number.isFinite(freePreviewCountRaw) && freePreviewCountRaw >= 0
		? Math.min(freePreviewCountRaw, pageCount)
		: Math.min(2, pageCount);

	return {
		fileName: data.fileName || '',
		file: normalizeSourceFile(data.file),
		price: data.price ?? '9.90',
		currency: data.currency || '¥',
		buttonText: data.buttonText || '解锁全部内容',
		buttonBg: data.buttonBg || data.buttonBgStart || '#285af9',
		freePreviewCount,
		trustText: data.trustText || '支付后永久有效，随时查看',
		footerText: data.footerText || '支付安全保障 | 如遇问题，请联系客服',
		footerFeatures: getFixedFooterFeatures(),
		items,
		borderRadius: Math.max(0, Number(data.borderRadius) || PAY_FILE_UNLOCK_DEFAULT_BORDER_RADIUS),
	};
}

export function getPayFileUnlockDefaults(extra = {}) {
	return normalizePayFileFields({
		items: [],
		...extra,
	});
}

export function getPayFileUnlockPreviewDefaults() {
	return getPayFileUnlockDefaults({
		fileName: 'AI客服智能体搭建手册.docx',
		file: {
			filesize: '4.05MB',
		},
		items: createPayFileUnlockDemoPreviewItems(6),
		freePreviewCount: 2,
	});
}

export function getPayFileUnlockMetaText(data = {}) {
	const normalized = normalizePayFileUnlockData(data);
	const pageCount = normalized.items.length || 0;
	const fileSize = normalized.file?.filesize || '';
	const parts = [getFileTypeLabel(normalized.fileName)];
	if (pageCount > 0) {
		parts.push(`${pageCount}页`);
	}
	if (fileSize) {
		parts.push(fileSize);
	}
	return parts.join(' · ');
}

export function normalizePayFileUnlockData(data = {}) {
	return normalizePayFileFields(data);
}

export function isPayFileUnlockBlock(type) {
	return type === PAY_FILE_UNLOCK_TYPE;
}

export function getPayFileUnlockPageCount(data = {}) {
	return normalizePayFileUnlockData(data).items.length;
}

export function getPayFileUnlockCardStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizePayFileUnlockData(data);
	const pxRatio = h5 ? 50 : 1;
	const radius = normalized.borderRadius;
	return {
		borderRadius: h5 ? `${radius / pxRatio}rem` : `${radius}px`,
	};
}

export function getPayFileUnlockFileIconMark(fileName = '') {
	const ext = (fileName.split('.').pop() || '').toLowerCase();
	if (ext === 'pdf') return 'PDF';
	if (['doc', 'docx'].includes(ext)) return 'W';
	if (['xls', 'xlsx'].includes(ext)) return 'X';
	if (['ppt', 'pptx'].includes(ext)) return 'PPT';
	return 'FILE';
}

export function getPayFileUnlockFileIconKey(fileName = '') {
	const ext = (fileName.split('.').pop() || '').toLowerCase();
	if (ext === 'pdf') return 'pdf';
	if (['doc', 'docx'].includes(ext)) return 'word';
	if (['xls', 'xlsx'].includes(ext)) return 'excel';
	if (['ppt', 'pptx'].includes(ext)) return 'powerpoint';
	if (ext === 'txt') return 'txt';
	if (['zip', 'rar', '7z'].includes(ext)) return 'zip';
	return 'file';
}

export function getPayFileUnlockUnlockKey(blockId) {
	return blockId ? `gedit-pay-file-unlock-${blockId}` : '';
}

export function isPayFileUnlockUnlocked(blockId) {
	const key = getPayFileUnlockUnlockKey(blockId);
	if (!key || typeof sessionStorage === 'undefined') return false;
	return sessionStorage.getItem(key) === '1';
}

export function setPayFileUnlockUnlocked(blockId, unlocked = true) {
	const key = getPayFileUnlockUnlockKey(blockId);
	if (!key || typeof sessionStorage === 'undefined') return;
	if (unlocked) {
		sessionStorage.setItem(key, '1');
	} else {
		sessionStorage.removeItem(key);
	}
}

export function getPayFileUnlockPreviewCards(data = {}) {
	const normalized = normalizePayFileUnlockData(data);
	const cards = [];
	const pageCount = normalized.items.length;
	const freeCount = normalized.freePreviewCount;

	normalized.items.slice(0, freeCount).forEach((item, index) => {
		cards.push({
			key: item.id,
			type: 'preview',
			index,
			image: item.image,
			locked: false,
		});
	});

	if (pageCount > freeCount) {
		const startPage = freeCount + 1;
		const endPage = pageCount;
		cards.push({
			key: 'locked-summary',
			type: 'locked',
			index: freeCount,
			image: normalized.items[freeCount]?.image
				|| normalized.items[freeCount - 1]?.image
				|| null,
			locked: true,
		});
	}

	return cards;
}

export function createPayFileUnlockPreviewItemsFromPages(pages = [], fileName = '') {
	return pages.map((page, index) => ({
		id: uuidv4(),
		image: normalizeUploadedImageItem({
			id: page.id,
			filename: page.filename || `${fileName || 'page'}-${index + 1}.jpg`,
			previewUrl: page.previewUrl || page.url || '',
			url: page.url || page.previewUrl || '',
			filesize: page.filesize,
			filesize_byte: page.filesize_byte ?? page.filesizeByte,
		}),
	}));
}
